/**
 * MenuDirectoryPage — shown at the restaurant root when multiple menus exist.
 * Hero cards: menu banner image + gradient overlay when available, icon fallback when not.
 */

import { useTranslation } from 'react-i18next'
import { ArrowRight, Package, Truck, UtensilsCrossed, Menu } from 'lucide-react'
import { ThemeSwitcher } from '@/components/ui'
import { useMenuData } from '@/hooks/useMenuData'
import { formatScheduleHint } from '@/utils/scheduleFormat'

const CHANNEL_LABEL_KEYS: Record<string, string> = {
  delivery: 'channelDelivery',
  pickup: 'channelPickup',
  in_store: 'channelInStore',
  other: 'channelOther',
}

const CHANNEL_ICONS: Record<string, React.ElementType> = {
  delivery: Truck,
  pickup: Package,
  in_store: UtensilsCrossed,
  other: Menu,
}

interface MenuEntry {
  id: string
  title: string
  slug?: string
  channel?: string
  banner_image_url?: string | null
  schedule_valid_from?: string | null
  schedule_valid_until?: string | null
  schedule_available_from_time?: string | null
  schedule_available_until_time?: string | null
  schedule_days?: number[]
}

interface MenuDirectoryPageProps {
  menus: MenuEntry[]
  onNavigate: (path: string) => void
}

export function MenuDirectoryPage({ menus, onNavigate }: MenuDirectoryPageProps) {
  const { t } = useTranslation()
  const { restaurant } = useMenuData()
  const restaurantName = restaurant?.title || ''
  const logoUrl = restaurant?.logo_url
  const bannerUrl = restaurant?.banner_image_url

  return (
    <div className="min-h-screen bg-background">
      {/* Theme toggle */}
      <div className="fixed top-3 right-3 z-30">
        <ThemeSwitcher />
      </div>

      {/* Restaurant banner */}
      {bannerUrl && (
        <div className="relative h-[140px] sm:h-[180px] overflow-hidden">
          <img
            src={bannerUrl}
            alt={`${restaurantName} banner`}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent" />
        </div>
      )}

      {/* Header */}
      <div className={`px-4 sm:px-8 ${bannerUrl ? '-mt-10 relative z-10' : 'pt-10 sm:pt-14'}`}>
        <div className="max-w-lg mx-auto flex flex-col items-center text-center gap-3">
          {logoUrl && (
            <img
              src={logoUrl}
              alt={`${restaurantName} logo`}
              className="h-16 w-16 sm:h-20 sm:w-20 rounded-2xl object-cover border-2 border-background shadow-lg"
            />
          )}
          {restaurantName && (
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground leading-tight">
              {restaurantName}
            </h1>
          )}
          <p className="text-sm sm:text-base text-muted-foreground">
            {t('common:chooseAMenu')}
          </p>
        </div>
      </div>

      {/* Menu cards */}
      <div className="max-w-lg mx-auto px-4 sm:px-8 py-6 sm:py-8 space-y-3">
        {menus.map((menu) => {
          const Icon = CHANNEL_ICONS[menu.channel ?? ''] ?? Menu
          const labelKey = menu.channel ? CHANNEL_LABEL_KEYS[menu.channel] : null
          const label = labelKey ? t(`common:${labelKey}`) : (menu.channel || null)
          const hasImage = Boolean(menu.banner_image_url)

          const scheduleHint = formatScheduleHint(menu, restaurant?.timezone)

          return (
            <button
              key={menu.id}
              onClick={() => menu.slug && onNavigate(`/${menu.slug}`)}
              disabled={!menu.slug}
              className="group relative w-full overflow-hidden rounded-2xl text-left transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              {hasImage ? (
                /* Image hero card */
                <div className="relative h-36 sm:h-40">
                  <img
                    src={menu.banner_image_url!}
                    alt={menu.title}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
                    loading="lazy"
                    decoding="async"
                  />
                  {/* Dark gradient for legibility */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/30 to-black/10" />

                  {/* Channel badge */}
                  {label && (
                    <div className="absolute top-3 left-3 flex items-center gap-1.5 rounded-full bg-black/50 backdrop-blur-sm px-2.5 py-1">
                      <Icon className="h-3 w-3 text-white/90" />
                      <span className="text-[11px] font-medium text-white/90">{label}</span>
                    </div>
                  )}

                  {/* Title + schedule hint + arrow */}
                  <div className="absolute bottom-0 left-0 right-0 flex items-end justify-between px-4 pb-3.5">
                    <div>
                      <p className="font-bold text-lg text-white leading-tight drop-shadow-sm">
                        {menu.title}
                      </p>
                      {scheduleHint && (
                        <p className="text-xs text-white/70 mt-0.5">{scheduleHint}</p>
                      )}
                    </div>
                    <ArrowRight className="h-5 w-5 text-white/80 shrink-0 mb-0.5 transition-transform duration-200 group-hover:translate-x-1" />
                  </div>
                </div>
              ) : (
                /* Icon fallback card */
                <div className="flex items-center gap-4 rounded-2xl border border-border bg-card px-5 py-4 shadow-sm hover:border-primary/40 hover:bg-accent transition-colors duration-200">
                  <div className="flex-shrink-0 h-11 w-11 rounded-xl bg-primary/10 flex items-center justify-center">
                    <Icon className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0 space-y-0.5">
                    <p className="font-semibold text-foreground leading-snug truncate">{menu.title}</p>
                    {label && <p className="text-xs font-medium text-muted-foreground">{label}</p>}
                    {scheduleHint && (
                      <p className="text-xs text-muted-foreground mt-0.5">{scheduleHint}</p>
                    )}
                  </div>
                  <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0 transition-transform duration-200 group-hover:translate-x-1" />
                </div>
              )}
            </button>
          )
        })}
      </div>
    </div>
  )
}
